import { useMemo } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LINK_TYPES } from 'MeetingsBase/constants/enums';
import { List } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * number if specific user selected (except self)
 * ALL_MEETINGS_USERS if all users selected
 * null if self selected
 */
export const isViewingOwnLinks = (selectedUserOption, meetingsUser) => !!(meetingsUser && (!selectedUserOption || selectedUserOption === meetingsUser.userId));

// We hide certain actions (delete) on non-owned links, *unless* the user is in admin mode
export const getIsNonOwnedLink = (link, meetingsUser) => {
  if (!meetingsUser) {
    return false;
  }
  return link.userId !== meetingsUser.userId;
};
const MeetingsTableActions = ({
  link,
  handleEditLinkClick,
  handleDeleteLinkClick,
  handleCopyButtonClick,
  handleSetAsDefaultButtonClick,
  handleOpenEmbedModal,
  selectedUserOption,
  meetingsUser,
  canEditMoreThanOneLink,
  canEditAllLinks,
  handleOpenCloneModal,
  hasStarterFeatureAccess,
  isJitaUser
}) => {
  const history = useHistory();
  const renderedCopyButton = useMemo(() => {
    return /*#__PURE__*/_jsx(CopyButton, {
      use: "tertiary-light",
      size: "extra-small",
      value: link.link,
      onClick: handleCopyButtonClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.copyLink"
      })
    }, `${link.id}-copy-btn`);
  }, [link, handleCopyButtonClick]);
  const renderedEditButton = useMemo(() => {
    const isNonOwnedLink = getIsNonOwnedLink(link, meetingsUser);
    const disabledForEditPermission = !isJitaUser && !canEditAllLinks && isNonOwnedLink;
    return /*#__PURE__*/_jsx(Tooltip, {
      disabled: !disabledForEditPermission,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.editPermissionsTooltip"
      }),
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "edit-button",
        id: "edit-button",
        use: "tertiary-light",
        size: "extra-small",
        onClick: () => {
          handleEditLinkClick();
          history.push(`link/${link.id}`);
        },
        "data-onboarding": "edit-link-button",
        "data-selenium-test": "edit-link-button",
        disabled: disabledForEditPermission,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "adminHeader.editLink"
        })
      }, `${link.id}-edit-btn`)
    }, `${link.id}-edit-btn-tooltip`);
  }, [isJitaUser, meetingsUser, canEditAllLinks, link, handleEditLinkClick, history]);
  const renderedEmbedButton = useMemo(() => {
    return /*#__PURE__*/_jsx(Button, {
      "data-selenium-test": "embed-link-button",
      onClick: handleOpenEmbedModal,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.embedCode"
      })
    }, `${link.id}-embed-btn`);
  }, [handleOpenEmbedModal, link.id]);
  const renderedCloneButton = useMemo(() => {
    const canClone = !isJitaUser && link.type === LINK_TYPES.get('personal') && hasStarterFeatureAccess && !link.paymentLinkId;
    if (!canClone) {
      return null;
    }
    return /*#__PURE__*/_jsx(Button, {
      "data-test-id": "clone-button",
      id: "clone-button",
      onClick: handleOpenCloneModal,
      "data-selenium-test": "clone-meeting-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.cloneLink"
      })
    }, `${link.id}-clone-btn`);
  }, [handleOpenCloneModal, hasStarterFeatureAccess, isJitaUser, link.id, link.paymentLinkId, link.type]);
  const renderedDeleteButton = useMemo(() => {
    if (link.isDefaultLink) {
      return null;
    }
    const isNonOwnedLink = getIsNonOwnedLink(link, meetingsUser);
    const disabledForEditPermission = !canEditAllLinks && isNonOwnedLink;
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "right",
      disabled: !disabledForEditPermission,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.deletePermissionsTooltip"
      }),
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "delete-button",
        disabled: disabledForEditPermission,
        id: "delete-button",
        onClick: handleDeleteLinkClick,
        "data-selenium-test": "delete-link-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "adminHeader.deleteLink"
        })
      }, `${link.id}-delete-btn`)
    });
  }, [canEditAllLinks, handleDeleteLinkClick, link, meetingsUser]);
  const renderedSetAsDefaultButton = useMemo(() => {
    const isNonOwnedLink = getIsNonOwnedLink(link, meetingsUser);
    const hiddenForEditPermission = !canEditAllLinks && isNonOwnedLink;
    if (hiddenForEditPermission) {
      return null;
    }
    const disableForUpgrade = !link.isDefaultLink && !canEditMoreThanOneLink;
    if (link.isDefaultLink || selectedUserOption !== null && !isViewingOwnLinks(selectedUserOption, meetingsUser) && !canEditAllLinks) {
      return null;
    }
    return /*#__PURE__*/_jsx(Button, {
      onClick: handleSetAsDefaultButtonClick,
      "data-selenium-test": "set-as-default-button",
      disabled: disableForUpgrade,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.setAsDefault"
      })
    }, `${link.id}-setAsDefault-btn`);
  }, [canEditAllLinks, canEditMoreThanOneLink, handleSetAsDefaultButtonClick, link, meetingsUser, selectedUserOption]);
  const renderedOtherActionButtons = useMemo(() => {
    return [renderedEmbedButton, renderedCloneButton, renderedSetAsDefaultButton, renderedDeleteButton];
  }, [renderedCloneButton, renderedDeleteButton, renderedEmbedButton, renderedSetAsDefaultButton]);
  const renderedActionDropdown = useMemo(() => {
    const dropdownButton = /*#__PURE__*/_jsx(Dropdown, {
      "data-test-id": "actions-dropdown",
      id: "action-dropdown",
      closeOnMenuClick: true,
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.table.actions.dropdown"
      }),
      buttonUse: "tertiary-light",
      buttonSize: "extra-small",
      "data-selenium-test": "meetings-link-actions-dropdown",
      children: /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        children: renderedOtherActionButtons
      })
    });
    return dropdownButton;
  }, [renderedOtherActionButtons]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderedEditButton, renderedCopyButton, renderedActionDropdown]
  });
};
export default MeetingsTableActions;